import { useEffect, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import type { MovementPlan } from '../motion/movementPlan'
import { TaxiPathRenderer, type ActiveRibbon } from '../services/TaxiPathRenderer'

/**
 * Draw faint ground corridors under the currently-moving actors' taxi/tow legs
 * when `enablePathRibbons` is on. Pure function of the clock — ribbons appear and
 * clear as the active leg set changes while playing or scrubbing.
 */
export function useTaxiPathRibbons(
  layerService: MapboxCustomLayerService | null,
  plans: Record<string, MovementPlan>
) {
  const currentTime = useEventPlannerStore((s) => s.currentTime)
  const motionConfig = useEventPlannerStore((s) => s.motionConfig)
  const enable = motionConfig.enableAnimation && motionConfig.enablePathRibbons
  const rendererRef = useRef<TaxiPathRenderer | null>(null)

  useEffect(() => {
    if (!layerService) return
    const scene = layerService.getSceneObjects().scene
    if (!scene) return
    const r = new TaxiPathRenderer(scene)
    rendererRef.current = r
    return () => {
      r.dispose()
      rendererRef.current = null
    }
  }, [layerService])

  useEffect(() => {
    const r = rendererRef.current
    if (!r || !layerService) return
    if (!enable) {
      r.sync([])
      layerService.markSceneDirty()
      return
    }
    const active: ActiveRibbon[] = []
    for (const id in plans) {
      for (const leg of plans[id].legs) {
        if (leg.kind !== 'taxi' && leg.kind !== 'tow' && leg.kind !== 'tug_empty') continue
        if (currentTime >= leg.startT && currentTime < leg.endT) {
          active.push({ key: `${leg.actorId}:${leg.startT}`, path: leg.path, kind: leg.kind })
        }
      }
    }
    r.sync(active)
    layerService.markSceneDirty()
  }, [layerService, plans, currentTime, enable])
}
